import axios from 'axios';

/**
 * Algolia search requests, with failover across the app's cluster hosts.
 *
 * Algolia serves each application from a cluster of interchangeable nodes.
 * `{appId}-dsn.algolia.net` is a DNS round-robin over those nodes, so when one
 * node is down or unreachable a share of requests - a third, for a three-node
 * cluster - never reach a server at all. A request that gets no response also
 * gets no Access-Control-Allow-Origin header, so the browser reports it as a
 * CORS failure rather than a network error, and the search silently returns
 * nothing.
 *
 * The named hosts below address the same cluster and are what Algolia's own
 * clients retry against. Retrying there turns a dead node into a slightly
 * slower search instead of a broken one.
 */

const HOST_SUFFIXES = [
    '-dsn.algolia.net',
    '-1.algolianet.com',
    '-2.algolianet.com',
    '-3.algolianet.com',
];

/**
 * An unreachable host costs a full timeout, so once one has failed it is
 * skipped for a while rather than re-paying that timeout on every keystroke.
 */
const HOST_DOWN_MS = 2 * 60 * 1000;

/**
 * Without this a dead node leaves the request hanging on the browser's own
 * connect timeout (tens of seconds) and the dropdown spins the whole time.
 * Failing over quickly matters more than tolerating a slow node.
 */
const REQUEST_TIMEOUT_MS = 5000;

const downUntil = new Map();

function hostsToTry(state) {
    const appId = state.VUE_APP_ALGOLIA_DNS;
    const hosts = HOST_SUFFIXES.map((suffix) => `${appId}${suffix}`);
    const now = Date.now();
    const live = hosts.filter((host) => !(downUntil.get(host) > now));
    // Every host marked down means the outage is wider than one node (or the
    // client is offline). Try them all again rather than failing without
    // sending anything - the marks are a latency optimisation, not a verdict.
    return live.length ? live : hosts;
}

/**
 * A response - any response, 200 or 403 or 404 - means the host is reachable
 * and the caller should see it. Only a request that got no response at all is
 * worth retrying elsewhere.
 */
function isTransportError(error) {
    return !error.response;
}

function algoliaPost(state, path, body) {
    const headers = {
        'X-Algolia-API-Key': state.VUE_APP_ALGOLIA_API_KEY,
        'X-Algolia-Application-Id': state.VUE_APP_ALGOLIA_APP_ID,
    };
    const hosts = hostsToTry(state);

    const attempt = (index) => {
        const host = hosts[index];
        return axios
            .post(`https://${host}${path}`, body, { headers, timeout: REQUEST_TIMEOUT_MS })
            .then((res) => {
                downUntil.delete(host);
                return res;
            })
            .catch((error) => {
                if (!isTransportError(error)) throw error;
                downUntil.set(host, Date.now() + HOST_DOWN_MS);
                if (index + 1 >= hosts.length) throw error;
                return attempt(index + 1);
            });
    };

    return attempt(0);
}

/**
 * Search one index. `params` is Algolia's query string, e.g.
 * `typoTolerance=false&query=amox`. Resolves with the axios response, so
 * callers keep reading `res.data.hits`.
 */
export function searchIndex(state, indexName, params) {
    return algoliaPost(state, `/1/indexes/${encodeURIComponent(indexName)}/query`, { params });
}

/**
 * Search several indexes in one request. `requests` is Algolia's multi-query
 * array of { indexName, params }. Resolves with the axios response, so callers
 * keep reading `res.data.results`.
 */
export function searchIndexes(state, requests) {
    return algoliaPost(state, '/1/indexes/*/queries', { requests });
}
